import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, X, CheckCircle, XCircle, PaperPlaneRight,
  PencilSimple, Trash, MagicWand, ArrowRight, Sparkle, ArrowsOutSimple,
  ArrowClockwise, ClipboardText, FileText, Eraser, Check, LinkSimple, Scales,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import RichTextEditor from "@/components/RichTextEditor";

const STATUS_META = {
  rascunho: { label: "Rascunho", bg: "bg-[#334155]/40", text: "text-[#94A3B8]", border: "border-[#334155]" },
  em_analise: { label: "Em Análise", bg: "bg-[#FFDF00]/15", text: "text-[#FFDF00]", border: "border-[#FFDF00]/40" },
  aprovado: { label: "Aprovado", bg: "bg-[#009C3B]/15", text: "text-[#12B850]", border: "border-[#009C3B]/40" },
  rejeitado: { label: "Rejeitado", bg: "bg-[#EF4444]/15", text: "text-[#F87171]", border: "border-[#EF4444]/40" },
};

const CATEGORIAS = { bens: "Bens", servicos: "Serviços", obras: "Obras", tic: "TIC (Tecnologia)" };

const SUGESTAO_ACOES = [
  { tipo: "continuar", label: "Continuar texto", Icon: ArrowRight, color: "#009C3B",
    hint: "A IA redige a próxima seção a partir de onde você parou." },
  { tipo: "melhorar", label: "Melhorar redação", Icon: Sparkle, color: "#FFDF00",
    hint: "Refina linguagem técnico-jurídica mantendo o sentido." },
  { tipo: "expandir", label: "Expandir seção", Icon: ArrowsOutSimple, color: "#1E4FBF",
    hint: "Detalha mais o conteúdo com fundamentação legal." },
  { tipo: "conformidade", label: "Conformidade Lei 14.133", Icon: ClipboardText, color: "#F97316",
    hint: "Audita o texto e aponta lacunas por inciso." },
  { tipo: "resumir", label: "Resumir", Icon: FileText, color: "#7C3AED",
    hint: "Produz um resumo executivo curto do documento." },
  { tipo: "corrigir", label: "Revisar gramática", Icon: Eraser, color: "#06B6D4",
    hint: "Corrige erros preservando o conteúdo técnico." },
];

/**
 * Página genérica para ETP e TR.
 */
export default function PlanejamentoDocModule({
  endpoint, title, subtitle, baseLegal, promoverLabel, promoverPath, nextRoute, Icon, acentoColor = "#FFDF00",
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [municipioId, setMunicipioId] = useState("");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (isAdmin && municipioId) p.set("municipio_id", municipioId);
    api.get(`/${endpoint}?${p}`).then((r) => setItems(r.data?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, municipioId]);
  useEffect(() => {
    if (isAdmin) api.get("/municipalities").then((r) => setMunicipios(r.data || []));
  }, [isAdmin]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <Icon size={12} weight="duotone" style={{ color: acentoColor }} /> {subtitle}
          </div>
          <h1 className="font-heading text-4xl mt-1">{title}</h1>
          <p className="text-xs text-[#64748B] mt-1 font-mono">{baseLegal}</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
                className="px-4 py-2.5 rounded-md bg-[#002776] hover:bg-[#1E4FBF] text-white font-semibold flex items-center gap-2 shadow-sm"
                data-testid={`${endpoint}-new`}>
          <Plus size={14} weight="bold" /> Novo
        </button>
      </div>

      <div className="flex gap-3 mb-4 items-end">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="tce-input px-3 py-2 rounded-md text-sm" data-testid={`${endpoint}-status`}>
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="em_analise">Em Análise</option>
          <option value="aprovado">Aprovado</option>
          <option value="rejeitado">Rejeitado</option>
        </select>
        {isAdmin && (
          <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)}
                  className="tce-input px-3 py-2 rounded-md text-sm">
            <option value="">Todos os municípios</option>
            {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
          </select>
        )}
      </div>

      <div className="tce-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] border-b border-[#334155]">
            <tr className="text-left text-[#94A3B8]">
              <th className="px-4 py-3 font-medium">Nº</th>
              <th className="px-4 py-3 font-medium">Título</th>
              <th className="px-4 py-3 font-medium">Categoria</th>
              <th className="px-4 py-3 font-medium">Vínculos</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-[#94A3B8]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-[#94A3B8]">
                Nenhum registro. Clique em <b>Novo</b> ou promova a partir da etapa anterior.
              </td></tr>
            )}
            {items.map((d) => {
              const sm = STATUS_META[d.status] || STATUS_META.rascunho;
              return (
                <tr key={d.id} className="border-b border-[#334155]/60 hover:bg-[#334155]/20"
                    data-testid={`${endpoint}-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.numero_gerado}</td>
                  <td className="px-4 py-3 max-w-[380px] truncate" title={d.titulo}>{d.titulo}</td>
                  <td className="px-4 py-3 text-xs">{CATEGORIAS[d.categoria] || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">
                    {d.demanda_id && <span className="mr-2">DFD✓</span>}
                    {d.etp_id && <span className="mr-2">ETP✓</span>}
                    {d.tr_id && <span className="mr-2">TR✓</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setEditing(d); setShowForm(true); }}
                            className="px-2 py-1 rounded border border-[#334155] hover:border-[#FFDF00] text-xs"
                            data-testid={`${endpoint}-open-${d.id}`}>
                      Abrir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <DocFormModal
          doc={editing} endpoint={endpoint} user={user} isAdmin={isAdmin} municipios={municipios}
          promoverLabel={promoverLabel} promoverPath={promoverPath} nextRoute={nextRoute}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function DocFormModal({ doc, endpoint, user, isAdmin, municipios, promoverLabel, promoverPath, nextRoute,
                         onClose, onSaved }) {
  const canApprove = user?.role === "admin" || user?.role === "gestor";
  const [current, setCurrent] = useState(doc);
  const [form, setForm] = useState({
    municipio_id: doc?.municipio_id || user?.municipio_id || "",
    titulo: doc?.titulo || "",
    categoria: doc?.categoria || "",
    content_html: doc?.content_html || "",
  });
  const [instrucoes, setInstrucoes] = useState("");
  const [parecer, setParecer] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [sugTipo, setSugTipo] = useState("");
  const [sugTexto, setSugTexto] = useState("");
  const [sugInstrucao, setSugInstrucao] = useState("");
  const [sugResult, setSugResult] = useState(null);
  const [sugBusy, setSugBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [cadeia, setCadeia] = useState(null);
  const isEdit = !!current;
  const sm = current ? STATUS_META[current.status] : null;
  const editable = !current || current.status === "rascunho" || current.status === "em_analise";
  const canUseAI = editable && !!current;

  // Carrega full doc com content_html
  useEffect(() => {
    if (current?.id && !current.content_html) {
      api.get(`/${endpoint}/${current.id}`).then((r) => {
        setCurrent(r.data);
        setForm((f) => ({ ...f, content_html: r.data.content_html || "" }));
      });
    }
  }, [current?.id, endpoint]);

  // Carrega a cadeia completa (vínculos) sempre que houver demanda_id
  useEffect(() => {
    const demandaId = current?.demanda_id;
    if (!demandaId) { setCadeia(null); return; }
    api.get(`/planejamento/cadeia/${demandaId}`)
      .then((r) => setCadeia(r.data))
      .catch(() => setCadeia(null));
  }, [current?.demanda_id, current?.status, current?.id]);

  const salvar = async () => {
    setBusy(true);
    try {
      if (!isEdit) {
        const r = await api.post(`/${endpoint}`, { ...form });
        toast.success(`${r.data.numero_gerado} criado`);
        setCurrent(r.data);
        setDirty(false);
      } else {
        const r = await api.patch(`/${endpoint}/${current.id}`, {
          titulo: form.titulo, categoria: form.categoria, content_html: form.content_html,
        });
        toast.success("Salvo");
        setCurrent(r.data);
        setDirty(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const gerarIA = async () => {
    if (!current) { toast.error("Salve primeiro para gerar via IA"); return; }
    if (form.content_html &&
        !window.confirm("Isto substituirá todo o conteúdo atual pelo texto gerado pela IA. Continuar?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/${endpoint}/${current.id}/gerar-ia`,
                                { instrucoes_adicionais: instrucoes || null });
      setCurrent(r.data);
      setForm((f) => ({ ...f, content_html: r.data.content_html || "" }));
      setDirty(false);
      toast.success("Conteúdo gerado pela IA — revise e salve");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro IA"); }
    finally { setBusy(false); }
  };

  const aplicarModelo = async () => {
    if (!current) { toast.error("Salve primeiro"); return; }
    if (form.content_html && form.content_html.length > 100 &&
        !window.confirm("Isto substituirá o conteúdo atual pelo modelo padrão. Continuar?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/${endpoint}/${current.id}/aplicar-modelo`);
      setCurrent(r.data);
      setForm((f) => ({ ...f, content_html: r.data.content_html || "" }));
      setDirty(false);
      toast.success("Modelo padrão aplicado (Lei 14.133 + TCU)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao aplicar modelo"); }
    finally { setBusy(false); }
  };

  const pedirSugestao = async (tipo, texto = "", instrucao = "") => {
    if (!current) { toast.error("Salve o documento primeiro"); return; }
    setSugBusy(true); setSugResult(null); setSugTipo(tipo);
    try {
      const r = await api.post(`/${endpoint}/${current.id}/sugestao-ia`, {
        tipo,
        texto_selecionado: texto || null,
        instrucao: instrucao || null,
      });
      setSugResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha na sugestão"); }
    finally { setSugBusy(false); }
  };

  const inserirSugestao = (modo) => {
    if (!sugResult?.sugestao_html) return;
    const atual = form.content_html || "";
    let novo = atual;
    if (modo === "append" || !sugTexto) {
      novo = (atual ? atual + "\n" : "") + sugResult.sugestao_html;
    } else if (modo === "replace" && sugTexto) {
      // Substitui a primeira ocorrência do trecho selecionado
      if (atual.includes(sugTexto)) {
        novo = atual.replace(sugTexto, sugResult.sugestao_html);
      } else {
        // fallback: anexa
        novo = (atual ? atual + "\n" : "") + sugResult.sugestao_html;
        toast.info("Trecho não encontrado — sugestão anexada ao final");
      }
    } else if (modo === "copy") {
      navigator.clipboard?.writeText(sugResult.sugestao_html.replace(/<[^>]+>/g, " ").trim());
      toast.success("Copiado para a área de transferência");
      return;
    }
    setForm((f) => ({ ...f, content_html: novo }));
    setDirty(true);
    setSugResult(null); setSugTexto(""); setSugInstrucao(""); setSugTipo("");
    toast.success("Sugestão aplicada — não esqueça de salvar");
  };

  const transicao = async (acao) => {
    if (acao === "rejeitar" && !parecer) { toast.error("Informe o parecer"); return; }
    if (dirty && (acao === "submeter" || acao === "aprovar")) {
      if (!window.confirm("Há alterações não salvas. Continuar sem salvar?")) return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/${endpoint}/${current.id}/${acao}`, { parecer: parecer || null });
      toast.success(`Status: ${r.data.status}`);
      setCurrent(r.data);
      setParecer("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const promover = async () => {
    if (!promoverPath) return;
    setBusy(true);
    try {
      const path = promoverPath.replace("{id}", current.id);
      const r = await api.post(path, {});
      toast.success(r.data?.message || "Promovido com sucesso");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao promover"); }
    finally { setBusy(false); }
  };

  const excluir = async () => {
    if (!current) { onClose(); return; }
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await api.delete(`/${endpoint}/${current.id}`);
      toast.success("Excluído");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const onChangeHtml = (html) => {
    setForm((f) => ({ ...f, content_html: html }));
    setDirty(true);
  };

  const wordCount = useMemo(() => {
    const txt = (form.content_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return txt ? txt.split(" ").length : 0;
  }, [form.content_html]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-7xl h-[94vh] bg-[#1E293B] border border-[#334155] rounded-lg flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="uppercase-label">
              {isEdit ? `${current?.numero_gerado}` : "Novo documento"}
              {dirty && <span className="ml-2 text-[#FFDF00]">● não salvo</span>}
            </div>
            <input value={form.titulo} onChange={(e) => { setForm({ ...form, titulo: e.target.value }); setDirty(true); }}
                    disabled={!editable} placeholder="Título descritivo"
                    className="font-heading text-2xl mt-1 bg-transparent border-none outline-none text-[#E9EEF5] w-full"
                    data-testid="doc-field-titulo" />
            {sm && (
              <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                {sm.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded"><X size={18} /></button>
        </div>

        {cadeia && <ChainBar cadeia={cadeia} currentCollection={endpoint} />}

        <div className="flex-1 overflow-hidden flex">
          {/* Coluna principal — Editor */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {isAdmin && !isEdit && (
                <div>
                  <label className="uppercase-label block mb-1">Município *</label>
                  <select value={form.municipio_id} onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                          className="tce-input w-full px-3 py-2 rounded-md text-sm">
                    <option value="">Selecione…</option>
                    {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="uppercase-label block mb-1">Categoria</label>
                <select value={form.categoria} onChange={(e) => { setForm({ ...form, categoria: e.target.value }); setDirty(true); }}
                        disabled={!editable}
                        className="tce-input w-full px-3 py-2 rounded-md text-sm">
                  <option value="">—</option>
                  {Object.entries(CATEGORIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="flex items-end justify-end">
                <div className="text-xs text-[#64748B] font-mono">{wordCount} palavras</div>
              </div>
            </div>

            {editable && (
              <div className="bg-[#0F172A] border border-[#FFDF00]/30 rounded-md p-3">
                <label className="uppercase-label flex items-center gap-1 mb-2">
                  <MagicWand size={11} className="text-[#FFDF00]" /> Gerar documento completo com IA
                </label>
                <div className="flex gap-2">
                  <input value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)}
                          placeholder="Instruções específicas (ex.: ênfase em sustentabilidade, prazo urgente…)"
                          className="tce-input flex-1 px-3 py-2 rounded-md text-sm" />
                  <button onClick={gerarIA} disabled={busy || !current}
                          className="px-4 py-2 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                          data-testid="doc-ia">
                    <MagicWand size={12} weight="bold" /> {busy ? "Gerando…" : current ? "Gerar completo" : "Salve primeiro"}
                  </button>
                </div>
                <div className="text-[10px] text-[#64748B] mt-1">
                  Substitui todo o conteúdo. Para refinar trechos específicos, use o <b>Assistente IA</b> ao lado.
                </div>
                {current && !(form.content_html && form.content_html.length > 100) && (
                  <button onClick={aplicarModelo} disabled={busy}
                          className="mt-2 w-full px-3 py-2 rounded-md border border-[#009C3B]/40 text-[#12B850] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[#009C3B]/10 transition"
                          data-testid="doc-aplicar-modelo">
                    <Scales size={12} weight="duotone" /> Usar modelo padrão (Lei 14.133/2021 + TCU)
                  </button>
                )}
              </div>
            )}

            <div>
              <label className="uppercase-label block mb-1">Conteúdo</label>
              <div className="h-[calc(100vh-430px)] min-h-[400px] rounded-md overflow-hidden">
                <RichTextEditor
                  content={form.content_html}
                  onChange={onChangeHtml}
                  editable={editable}
                  testid="doc-rich-editor"
                />
              </div>
            </div>

            {current && (
              <div className="border-t border-[#334155] pt-4">
                <div className="uppercase-label mb-2">Histórico</div>
                <div className="space-y-1.5 text-xs">
                  {(current.historico || []).map((h, i) => {
                    const smH = STATUS_META[h.status] || STATUS_META.rascunho;
                    return (
                      <div key={i} className="bg-[#0F172A] border border-[#334155] rounded px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded border ${smH.bg} ${smH.text} ${smH.border} mr-2`}>{smH.label}</span>
                        <span className="text-[#94A3B8]">{h.obs}</span>
                        {h.parecer && <div className="italic text-[#E9EEF5] mt-1">"{h.parecer}"</div>}
                        <div className="text-[#64748B] mt-0.5">por {h.por} · {new Date(h.em).toLocaleString("pt-BR")}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {current && current.status !== "aprovado" && (
              <div>
                <label className="uppercase-label block mb-1">Parecer</label>
                <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
                          className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none" />
              </div>
            )}
          </div>

          {/* Painel lateral — Assistente IA */}
          {showAI && (
            <AssistenteIAPanel
              acoes={SUGESTAO_ACOES}
              canUseAI={canUseAI}
              sugTipo={sugTipo} setSugTipo={setSugTipo}
              sugTexto={sugTexto} setSugTexto={setSugTexto}
              sugInstrucao={sugInstrucao} setSugInstrucao={setSugInstrucao}
              sugResult={sugResult} setSugResult={setSugResult}
              sugBusy={sugBusy}
              pedirSugestao={pedirSugestao}
              inserirSugestao={inserirSugestao}
              onClose={() => setShowAI(false)}
            />
          )}
        </div>

        <div className="p-4 border-t border-[#334155] flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            {current && current.status !== "aprovado" && (
              <button onClick={excluir} className="px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-xs flex items-center gap-1">
                <Trash size={13} /> Excluir
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {canUseAI && (
              <button onClick={() => setShowAI((s) => !s)}
                      className={`px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 border transition-colors ${
                        showAI
                          ? "bg-[#FFDF00] text-[#002776] border-[#FFDF00]"
                          : "bg-transparent text-[#FFDF00] border-[#FFDF00]/40 hover:bg-[#FFDF00]/10"
                      }`}
                      data-testid="toggle-assistente-ia">
                <Sparkle size={13} weight="duotone" /> {showAI ? "Fechar Assistente IA" : "Assistente IA"}
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm">Fechar</button>
            {editable && (
              <button onClick={salvar} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#002776] text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="doc-save">
                <PencilSimple size={13} /> {busy ? "Salvando…" : isEdit ? "Salvar" : "Criar"}
              </button>
            )}
            {current?.status === "rascunho" && (
              <button onClick={() => transicao("submeter")} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#FFDF00] text-[#002776] font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="doc-submit">
                <PaperPlaneRight size={13} /> Submeter
              </button>
            )}
            {current?.status === "em_analise" && canApprove && (
              <>
                <button onClick={() => transicao("rejeitar")} disabled={busy}
                        className="px-4 py-2 rounded-md border border-red-500 text-red-400 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  <XCircle size={13} /> Rejeitar
                </button>
                <button onClick={() => transicao("aprovar")} disabled={busy}
                        className="px-4 py-2 rounded-md bg-[#009C3B] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                  <CheckCircle size={13} /> Aprovar
                </button>
              </>
            )}
            {current?.status === "aprovado" && promoverPath && (
              <button onClick={promover} disabled={busy}
                      className="px-4 py-2 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                      data-testid="doc-promover">
                {promoverLabel} <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function AssistenteIAPanel({
  acoes, canUseAI, sugTipo, sugTexto, setSugTexto, sugInstrucao, setSugInstrucao,
  sugResult, sugBusy, pedirSugestao, inserirSugestao, onClose,
}) {
  return (
    <aside className="w-[380px] shrink-0 border-l border-[#334155] bg-[#0F172A] flex flex-col overflow-hidden"
           data-testid="assistente-ia-panel">
      <div className="p-3 border-b border-[#334155] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkle size={16} weight="duotone" className="text-[#FFDF00]" />
          <div>
            <div className="text-sm font-semibold text-[#E9EEF5]">Assistente IA</div>
            <div className="text-[10px] text-[#64748B]">Claude Sonnet 4.5 · Lei 14.133/2021</div>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-[#334155] rounded"><X size={14} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {!canUseAI && (
          <div className="bg-[#334155]/40 border border-[#334155] rounded p-3 text-xs text-[#94A3B8]">
            Salve o documento primeiro para habilitar as sugestões de IA.
          </div>
        )}

        <div>
          <label className="uppercase-label block mb-1.5">Trecho selecionado (opcional)</label>
          <textarea value={sugTexto} onChange={(e) => setSugTexto(e.target.value)}
                    rows={3} placeholder="Cole aqui o trecho que a IA deve refinar/expandir. Deixe em branco para agir sobre todo o documento."
                    className="tce-input w-full px-2 py-1.5 rounded text-xs resize-none"
                    data-testid="sug-texto" />
        </div>

        <div>
          <label className="uppercase-label block mb-1.5">Instrução adicional</label>
          <input value={sugInstrucao} onChange={(e) => setSugInstrucao(e.target.value)}
                 placeholder="Ex.: ênfase em sustentabilidade; incluir SLA 99,5%…"
                 className="tce-input w-full px-2 py-1.5 rounded text-xs"
                 data-testid="sug-instrucao" />
        </div>

        <div>
          <div className="uppercase-label mb-1.5">Ações rápidas</div>
          <div className="grid grid-cols-2 gap-1.5">
            {acoes.map((a) => (
              <button key={a.tipo} onClick={() => pedirSugestao(a.tipo, sugTexto, sugInstrucao)}
                      disabled={!canUseAI || sugBusy}
                      title={a.hint}
                      className="group bg-[#1E293B] border border-[#334155] hover:border-[#FFDF00] rounded p-2 text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid={`sug-acao-${a.tipo}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <a.Icon size={13} weight="duotone" style={{ color: a.color }} />
                  <span className="text-[11px] font-semibold text-[#E9EEF5]">{a.label}</span>
                </div>
                <div className="text-[9px] text-[#64748B] leading-tight">{a.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {sugInstrucao && canUseAI && (
          <button onClick={() => pedirSugestao("custom", sugTexto, sugInstrucao)}
                  disabled={sugBusy}
                  className="w-full px-3 py-2 rounded bg-[#002776] hover:bg-[#1E4FBF] text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="sug-custom">
            <MagicWand size={12} weight="bold" /> Executar instrução personalizada
          </button>
        )}

        {sugBusy && (
          <div className="bg-[#1E293B] border border-[#FFDF00]/40 rounded p-3 text-center">
            <div className="animate-spin inline-block w-5 h-5 border-2 border-[#FFDF00] border-t-transparent rounded-full mb-2" />
            <div className="text-xs text-[#94A3B8]">Consultando IA…</div>
          </div>
        )}

        {sugResult?.sugestao_html && !sugBusy && (
          <div className="bg-[#1E293B] border border-[#009C3B]/40 rounded overflow-hidden">
            <div className="px-3 py-2 bg-[#009C3B]/10 border-b border-[#009C3B]/30 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-[#12B850] font-semibold">
                <CheckCircle size={13} weight="duotone" /> Sugestão pronta ({sugTipo})
              </div>
              <button onClick={() => pedirSugestao(sugTipo, sugTexto, sugInstrucao)}
                      title="Regenerar"
                      className="p-1 hover:bg-[#334155] rounded">
                <ArrowClockwise size={12} />
              </button>
            </div>
            <div className="p-3 max-h-[260px] overflow-y-auto text-xs text-[#E9EEF5] leading-relaxed ai-response"
                 dangerouslySetInnerHTML={{ __html: sugResult.sugestao_html }}
                 data-testid="sug-preview" />
            <div className="p-2 border-t border-[#334155] flex flex-wrap gap-1.5">
              <button onClick={() => inserirSugestao("append")}
                      className="flex-1 px-2 py-1.5 rounded bg-[#009C3B] hover:bg-[#12B850] text-white text-[11px] font-semibold flex items-center justify-center gap-1"
                      data-testid="sug-aplicar-append">
                <Check size={11} weight="bold" /> Inserir no final
              </button>
              {sugTexto && (
                <button onClick={() => inserirSugestao("replace")}
                        className="flex-1 px-2 py-1.5 rounded bg-[#002776] hover:bg-[#1E4FBF] text-white text-[11px] font-semibold flex items-center justify-center gap-1"
                        data-testid="sug-aplicar-replace">
                  <Check size={11} weight="bold" /> Substituir trecho
                </button>
              )}
              <button onClick={() => inserirSugestao("copy")}
                      className="px-2 py-1.5 rounded border border-[#334155] text-[#94A3B8] text-[11px] hover:bg-[#334155]"
                      data-testid="sug-aplicar-copy">
                Copiar
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}


// ============================================================
// ChainBar — barra horizontal mostrando a cadeia DFD→ETP→TR→Matriz→PCA
// ============================================================
const _STATUS_COLORS = {
  rascunho: "border-[#334155] text-[#94A3B8] bg-[#334155]/30",
  em_analise: "border-[#FFDF00]/50 text-[#FFDF00] bg-[#FFDF00]/10",
  aprovado: "border-[#009C3B]/50 text-[#12B850] bg-[#009C3B]/15",
  aprovada: "border-[#009C3B]/50 text-[#12B850] bg-[#009C3B]/15",
  rejeitado: "border-red-500/50 text-red-400 bg-red-500/10",
  rejeitada: "border-red-500/50 text-red-400 bg-red-500/10",
};

function ChainBar({ cadeia, currentCollection }) {
  if (!cadeia) return null;
  const steps = [
    { key: "demandas", label: "DFD", data: cadeia.demanda, path: "/demandas" },
    { key: "etps", label: "ETP", data: cadeia.etp, path: "/etps" },
    { key: "trs", label: "TR", data: cadeia.tr, path: "/trs" },
    { key: "matrizes-riscos", label: "Matriz", data: cadeia.matriz, path: "/matrizes-riscos" },
    { key: "pca", label: "PCA",
      data: cadeia.pac_item && { numero_gerado: `#${String(cadeia.pac_item.numero_seq).padStart(4,"0")}`, status: "aprovado" },
      path: "/pac" },
  ];
  return (
    <div className="px-4 py-2.5 border-b border-[#334155] bg-[#0F172A]/60" data-testid="chain-bar">
      <div className="flex items-center gap-1.5 overflow-x-auto">
        <LinkSimple size={13} className="text-[#94A3B8] flex-shrink-0 mr-1" />
        <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold flex-shrink-0 mr-1">Vínculos</span>
        {steps.map((s, i) => (
          <ChainBarStep key={s.key} step={s} isCurrent={s.key === currentCollection} isLast={i === steps.length - 1} />
        ))}
      </div>
    </div>
  );
}

function ChainBarStep({ step, isCurrent, isLast }) {
  const d = step.data;
  const cls = d ? (_STATUS_COLORS[d.status] || _STATUS_COLORS.rascunho)
                : "border-dashed border-[#334155] text-[#64748B] bg-transparent";
  const inner = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono ${cls} ${isCurrent ? "ring-2 ring-[#FFDF00]/60" : ""}`}>
      <span className="font-sans font-semibold">{step.label}</span>
      {d ? <span className="opacity-80">{d.numero_gerado}</span> : <span className="opacity-60">—</span>}
    </span>
  );
  return (
    <>
      {d ? <Link to={step.path} className="hover:brightness-125 transition" title={d.titulo}>{inner}</Link> : inner}
      {!isLast && <ArrowRight size={9} className="text-[#334155] flex-shrink-0" />}
    </>
  );
}
